import Config from './Config.js';
import Helpers from './Helpers.js';
import Controllers from './Controllers.js';
import Events from './Events.js';
import Chat from './Chat.js';
import Book from './Book.js';
import Support from './Support.js';
import EventHandlers from './EventHandlers.js';

/**
 * Component — the Alpine data() factory for the connect bar. This is what
 * gets registered as the named Alpine component (see index.js):
 *
 *   Alpine.data('ThryvConnectBar', Component.data)
 *
 * Template: x-data='ThryvConnectBar(<?php echo json_encode($config) ?>)'
 */

var Component = new Object();

/**
 * Alpine x-data factory. Per-instance config comes from the template
 * (per-site phone number, debug flag).
 *
 * @param  {object} config  See template-parts/thryv-connect-bar/main.php
 * @return {object}         Alpine component state
 */
// NOTE: no `var _this = this` here — data is registered as a BARE reference
// (Alpine.data('ThryvConnectBar', Component.data) in index.js), so Alpine
// invokes it with ITS OWN context as `this`, not Component. Self-references
// in this file must stay explicit (`Component.init`).
Component.data = function (config = {}) {

  return {

    /**
     * Config — server-provided per-site state (phone number, toggles) with
     * the CP constants defaulting from Config.js; the template config can
     * still override those per instance.
     */
    phoneNumber: config.phoneNumber ?? '',
    phoneNumberHref: Controllers.phoneHref(config.phoneNumber ?? ''),
    country: config.country ?? 'US',
    debug: config.debug ?? false,
    bookingUrl: config.bookingUrl ?? Config.bookingUrl,
    chat: config.chat ?? Config.chat,
    chatEnabled: config.chatEnabled ?? false,
    oneMind: config.oneMind ?? Config.oneMind,
    oneMindEnabled: config.oneMindEnabled ?? false,

    /**
     * UI state
     */
    callModalOpen: false,
    expanded: true,
    bookModalOpen: false,
    bookLoaded: false, // becomes true on first Book click — mounts the CP iframe
    bookFrameLoading: false, // true while the CP page loads inside the iframe — drives the panel spinner
    bookExpanded: false, // panel resize toggle — widened so CP's fixed-width page fits scroll-free
    bookResizing: false, // true while the expand/shrink transition runs — fades the panel content
    phoneCopied: false, // brief "copied" feedback on the Call modal's copy button
    supportOpen: false, // Intercom messenger visibility — synced via Intercom's onShow/onHide
    oneMindOpen: false, // AI Advisor panel visibility
    oneMindLoaded: false, // becomes true on first AI Advisor click — renders the 1mind mount div
    oneMindState: 'idle', // idle | loading | ready | unavailable (1mind launcher lifecycle)
    oneMindExpanded: false, // panel resize toggle — the Superhuman wants far more width than the default panel
    oneMindResizing: false, // true while the expand/shrink transition runs — fades the panel content
    chatState: 'idle', // idle | loading | ready (Chat widget lazy-load lifecycle)
    chatOpen: false, // CP chat visibility — the bar's Chat button is the only launcher
    collapsed: Controllers.storedCollapsed(), // pill tucked into the round launcher — persists for the session
    scrollHidden: false, // slides away on scroll-down, back on scroll-up (EventHandlers.watchScroll)
    online: Controllers.online(Config.hours), // presence dot on Chat/Support (24/7 while Config.hours is null)

    /**
     * Event handler map — bound in the template as @click="evt.bookClick".
     * Each entry delegates to EventHandlers via Events.all().
     */
    evt: Events.all(),

    /**
     * Alpine lifecycle — runs once per component instance.
     */
    init() {

      // `this` here is the Alpine component instance (the state object
      // above) — pass it through to the one-time wiring.
      Component.init(this);

    },

  };

};

/**
 * Component wiring that is not per-render state — one-time setup on mount.
 * Keep DOM event concerns in the template (Alpine modifiers, e.g. the call
 * modal closes via @keydown.escape.window on the root element), not manual
 * listeners here.
 *
 * @param {object} component  The Alpine component (this) from data().init
 */
Component.init = function (component) {

  Helpers.log(component, 'init', component);

  // Chat: load the CP widget on first interaction or idle (when enabled) —
  // see Chat.schedule. The Chat button stays hidden until chatState is
  // 'ready', so it never dead-clicks while scripts load.
  Chat.schedule(component);

  // Book: arm the scheduler-iframe message listener (spinner sync +
  // widget-event placeholders; the iframe mounts on first Book click).
  Book.init(component);

  // Support: reposition Intercom for the bar and sync supportOpen as soon
  // as Intercom is available — a persisted-open messenger restored on page
  // load must not sit at the boot layer's launcher position.
  Support.init(component);

  // 1mind (AI Advisor): nothing to arm at init on purpose — the launcher
  // is injected lazily on the first click (OneMind.open), so bar pages
  // nobody clicks pay no download and open no 1mind session.

  // Scroll: tuck the bar away while the visitor reads downward, bring it
  // back on scroll-up.
  EventHandlers.watchScroll(component);

};

export default Component;
